import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  ButtonGroup,
  Form,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import type { User } from "firebase/auth";
import type { OrganizationRecord } from "../../api/organizations";
import {
  fetchAllOrganizationTopClients,
  fetchOrganizationTopClients,
  type TopClientRow,
  type TopClientsPeriodQuery,
  type TopClientsReport,
} from "../../api/topClients";
import { downloadTopClientsExcel } from "../../utils/topClientsExport";

interface TopClientsModalProps {
  show: boolean;
  onHide: () => void;
  org: OrganizationRecord | null;
  user: User;
}

const PAGE_SIZE = 25;
const PERIOD_DAYS = [30, 90, 365] as const;

function formatMoney(amount: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(amount || 0);
}

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export const TopClientsModal: React.FC<TopClientsModalProps> = ({
  show,
  onHide,
  org,
  user,
}) => {
  const [meta, setMeta] = useState<Omit<TopClientsReport, "clients"> | null>(
    null
  );
  const [clients, setClients] = useState<TopClientRow[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [periodMode, setPeriodMode] = useState<"days" | "range">("days");
  const [days, setDays] = useState<(typeof PERIOD_DAYS)[number]>(365);
  const [rangeFrom, setRangeFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 365);
    return toIsoDate(d);
  });
  const [rangeTo, setRangeTo] = useState(() => toIsoDate(new Date()));

  const periodQuery: TopClientsPeriodQuery = useMemo(() => {
    if (periodMode === "range") {
      return { mode: "range", dateFrom: rangeFrom, dateTo: rangeTo };
    }
    return { mode: "days", days };
  }, [periodMode, days, rangeFrom, rangeTo]);

  const resetAndLoad = useCallback(async () => {
    if (!org) return;
    setLoading(true);
    setError(null);
    setClients([]);
    setHasMore(false);
    setMeta(null);
    try {
      const data = await fetchOrganizationTopClients(user, org.id, {
        query: periodQuery,
        limit: PAGE_SIZE,
        offset: 0,
      });
      const { clients: firstPage, ...rest } = data;
      setMeta(rest);
      setClients(firstPage);
      setHasMore(data.hasMore);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load top clients");
      setClients([]);
      setMeta(null);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [org, periodQuery, user]);

  const loadMore = useCallback(async () => {
    if (!org || !hasMore || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const data = await fetchOrganizationTopClients(user, org.id, {
        query: periodQuery,
        limit: PAGE_SIZE,
        offset: clients.length,
      });
      setClients((prev) => {
        const seen = new Set(prev.map((row) => row.clientId));
        const appended = data.clients.filter((row) => !seen.has(row.clientId));
        return [...prev, ...appended];
      });
      setHasMore(data.hasMore);
      setMeta((prev) =>
        prev
          ? {
              ...prev,
              totalClients: data.totalClients,
              totalRevenueWithTax: data.totalRevenueWithTax,
              totalNetRevenue: data.totalNetRevenue,
              totalOrderCount: data.totalOrderCount,
              hasMore: data.hasMore,
            }
          : prev
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load more clients");
    } finally {
      setLoadingMore(false);
    }
  }, [org, hasMore, loadingMore, periodQuery, user, clients.length]);

  useEffect(() => {
    if (!show || !org) return;
    void resetAndLoad();
  }, [show, org?.id, periodQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExport = async () => {
    if (!org) return;
    setExporting(true);
    setExportProgress("Fetching all ranked clients…");
    setError(null);
    try {
      const full = await fetchAllOrganizationTopClients(
        user,
        org.id,
        periodQuery,
        200,
        20000
      );
      if (full.clients.length === 0) {
        setError("No clients with sales in this period to export.");
        return;
      }
      setExportProgress(`Building Excel (${full.clients.length} rows)…`);
      // Keep the table in sync with the full export when possible.
      setClients(full.clients);
      setHasMore(false);
      const { clients: _ignored, ...rest } = full;
      setMeta(rest);
      downloadTopClientsExcel(full);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to export");
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          Top clients{org ? ` — ${org.name}` : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          Clients ranked by approved sales revenue (highest first). Load more to
          browse the list, or export everything in one Excel file for marketing.
        </p>

        <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
          <ButtonGroup size="sm">
            {PERIOD_DAYS.map((value) => (
              <Button
                key={value}
                variant={
                  periodMode === "days" && days === value
                    ? "primary"
                    : "outline-secondary"
                }
                onClick={() => {
                  setPeriodMode("days");
                  setDays(value);
                }}
              >
                {value}d
              </Button>
            ))}
            <Button
              variant={periodMode === "range" ? "primary" : "outline-secondary"}
              onClick={() => setPeriodMode("range")}
            >
              Custom
            </Button>
          </ButtonGroup>

          {periodMode === "range" ? (
            <>
              <Form.Group controlId="top-clients-from" className="mb-0">
                <Form.Label className="small mb-0">From</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={rangeFrom}
                  onChange={(e) => setRangeFrom(e.target.value)}
                />
              </Form.Group>
              <Form.Group controlId="top-clients-to" className="mb-0">
                <Form.Label className="small mb-0">To</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={rangeTo}
                  onChange={(e) => setRangeTo(e.target.value)}
                />
              </Form.Group>
            </>
          ) : null}

          <Button
            variant="success"
            size="sm"
            className="ms-auto"
            disabled={!org || exporting || loading}
            onClick={() => void handleExport()}
          >
            {exporting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {exportProgress || "Exporting…"}
              </>
            ) : (
              "Export all to Excel"
            )}
          </Button>
        </div>

        {error ? <Alert variant="danger">{error}</Alert> : null}

        {loading && clients.length === 0 ? (
          <div className="d-flex align-items-center gap-2 text-muted py-4">
            <Spinner animation="border" size="sm" />
            <span>Loading ranking…</span>
          </div>
        ) : null}

        {meta ? (
          <>
            <div className="d-flex flex-wrap gap-3 small text-muted mb-2">
              <span>{meta.periodLabel}</span>
              <span>
                Showing {clients.length}
                {meta.totalClients > 0 ? ` of ${meta.totalClients}` : ""} clients
              </span>
              <span>
                {formatMoney(meta.totalRevenueWithTax, meta.currency)} sales
              </span>
              <span>{meta.totalOrderCount} orders</span>
            </div>

            {clients.length === 0 ? (
              <div className="text-muted py-3">
                No approved sales with clients in this period.
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover size="sm" className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Client</th>
                      <th>Contact</th>
                      <th>Creator</th>
                      <th className="text-end">Orders</th>
                      <th className="text-end">Revenue</th>
                      <th>Last order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((row) => (
                      <tr key={row.clientId}>
                        <td>{row.rank}</td>
                        <td>
                          <div className="fw-semibold">
                            {row.clientName || row.businessName || "—"}
                          </div>
                          <div className="text-muted small">
                            {[row.clientCode, row.rif].filter(Boolean).join(" · ") ||
                              row.clientId}
                          </div>
                          <div className="text-muted small">
                            {[row.city, row.zone, row.address]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        </td>
                        <td className="small">
                          <div>{row.email || "—"}</div>
                          <div>
                            {row.mobile || row.phone || row.officePhone || "—"}
                          </div>
                        </td>
                        <td className="small">
                          {row.creatorName || row.creatorSalesPerson || "—"}
                        </td>
                        <td className="text-end">{row.orderCount}</td>
                        <td className="text-end">
                          {formatMoney(row.revenueWithTax, meta.currency)}
                        </td>
                        <td className="small">{row.lastOrderDate || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            <div className="d-flex align-items-center justify-content-between mt-3 gap-2 flex-wrap">
              <span className="small text-muted">
                {hasMore
                  ? `${meta.totalClients - clients.length} more available`
                  : clients.length > 0
                    ? "All loaded clients shown"
                    : null}
              </span>
              {hasMore ? (
                <Button
                  size="sm"
                  variant="outline-primary"
                  disabled={loadingMore || loading}
                  onClick={() => void loadMore()}
                >
                  {loadingMore ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading…
                    </>
                  ) : (
                    `Load more (${PAGE_SIZE})`
                  )}
                </Button>
              ) : null}
            </div>
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TopClientsModal;
